/**
 * Resolve the "back to candidates" URL for detail and create pages.
 *
 * The list page (`CasesPage`) writes its current URL — including
 * active filter query params, workflow tab, pagination — into
 * `location.state.from` when it links to a case or the create
 * form. The child page reads it back through this helper and
 * passes it to `navigate(...)` on Back / Cancel actions, restoring
 * the reviewer's filters verbatim.
 *
 * The helper guards against three failure modes:
 *
 *   1. **Missing state** — direct URL, bookmark, refresh from a
 *      fresh tab. `location.state` is `null`; return the bare list
 *      URL so the reviewer still lands somewhere sensible.
 *   2. **Non-string `from`** — a rogue caller wrote an object /
 *      number. Ignore it and use the fallback.
 *   3. **Cross-page or off-app `from`** — a URL that doesn't
 *      start with `/onboarding/candidates` shouldn't be honoured;
 *      the Back button is scoped to that flow. Blocks a
 *      hypothetically-malicious `state.from = "https://evil.tld"`
 *      too, though React Router's `navigate` treats that as a
 *      relative path so the exploit surface is thin — this is
 *      defence in depth.
 *
 * Kept as a plain function (not a hook) so it composes freely
 * inside `useMemo`, tests, and any future non-React caller
 * (e.g. programmatic redirects from a global error boundary).
 */
export const CANDIDATES_LIST_PATH = '/onboarding/candidates';

export function resolveBackToListUrl(locationState: unknown): string {
  if (!locationState || typeof locationState !== 'object') return CANDIDATES_LIST_PATH;
  const from = (locationState as { from?: unknown }).from;
  if (typeof from !== 'string') return CANDIDATES_LIST_PATH;
  if (!from.startsWith(CANDIDATES_LIST_PATH)) return CANDIDATES_LIST_PATH;
  return from;
}
